<template>
    <draggable
        :model-value="children"
        @update:model-value="updateChildren"
        group="elementosPagina"
        :style="dropzoneStyle"
        item-key="componenteId"
        :clone="handleAddComponente"
        :class="dropzoneClasses"
        v-bind="$attrs"
    >
        <template #item="{ element }">
            <div
                class="dropzone-item"
                :style="{ 'align-self': element.props.alinhamento }"
                @mouseenter="elementoEmHover = element.componenteId"
                @mouseleave="elementoEmHover = null"
            >
                <component
                    :is="tabelaDePesquisaComponente[element.componente]?.componente"
                    v-bind="{ ...element.props, showIcon: element.componente !== 'cpf' }"
                    :itens="element.children"
                    :key="element.componenteId"
                    :isEditor="true"
                    :fontSize="element.props.fontSize"
                    :componenteId="element.componenteId"
                    @update:props="(props) => atualizarPropsComponente(element.componenteId, props)"
                    style="position: relative; overflow: visible"
                >
                    <template #overlaySelecao>
                        <OverlaySelecao
                            style="z-index: 10"
                            :titulo="tabelaDePesquisaComponente[element.componente]?.titulo || element.componente"
                            :selecionado="idElementoSelecionado === element.componenteId"
                            :mostrarCabecalho="elementoEmHover === element.componenteId"
                            :acoes="
                                element.componente === 'formulario'
                                    ? [{ icone: 'delete', acao: () => removerComponente(element.componenteId) }]
                                    : [
                                          { icone: 'edit_square', acao: () => selecionarElemento(element.componenteId) },
                                          { icone: 'delete', acao: () => removerComponente(element.componenteId) },
                                      ]
                            "
                        />
                    </template>
                    <template #acoes>
                        <AcoesCampos
                            :editavel="tabelaDePesquisaComponente[element.componente]?.editavel"
                            :deletavel="tabelaDePesquisaComponente[element.componente]?.deletavel"
                            @editar="selecionarElemento(element.componenteId)"
                            @remover="removerComponente(element.componenteId)"
                        />
                    </template>
                </component>
            </div>
        </template>
        <template #footer>
            <div v-if="!children.length" :class="placeholderClasses">
                <slot name="placeholder">
                    <span class="material-symbols-outlined" style="font-size: 36px">swipe_right</span>
                    <span>Arraste os elementos para essa área e solte</span>
                </slot>
            </div>
        </template>
    </draggable>
</template>

<script setup>
import { defineProps, computed, ref } from 'vue';
import { useStore } from 'vuex';
import draggable from 'vuedraggable';
import { useEditorLandingPage } from '@/composables/useEditorLandingPage';
import { LAYOUTS_DISPONIVEIS, COMPONENTES_DISPONIVEIS, CAMPOS_DISPONIVEIS, OUTROS_CAMPOS } from '@/constants/componentesCatalogo';
import OverlaySelecao from '@/components/editorLandingPage/ui/OverlaySelecao.vue';
import AcoesCampos from '@/components/editorLandingPage/ui/AcoesCampos.vue';

const props = defineProps({
    componenteId: {
        type: String,
        required: true,
    },
    dropzoneStyle: {
        type: Object,
    },
    dropzoneClass: {
        type: [String, Array, Object],
        default: 'dropzone-default',
    },
    placeholderClass: {
        type: [String, Array, Object],
        default: 'dropzone-placeholder-default',
    },
    itemClass: {
        type: [String, Array, Object],
        default: '',
    },
});

const store = useStore();
const { idElementoSelecionado, selecionarElemento, removerComponente, criarNovoComponente, obterChildrenPorId } = useEditorLandingPage();

const elementoEmHover = ref(null);

const tabelaDePesquisaComponente = [...COMPONENTES_DISPONIVEIS, ...LAYOUTS_DISPONIVEIS, ...CAMPOS_DISPONIVEIS, ...OUTROS_CAMPOS].reduce((acc, comp) => {
    acc[comp.componente] = { componente: comp.elemento, titulo: comp.titulo, editavel: comp.editavel, deletavel: comp.deletavel };
    return acc;
}, {});

const children = computed(() => {
    return obterChildrenPorId(props.componenteId);
});

const dropzoneClasses = computed(() => {
    const baseClasses = ['dropzone'];
    if (props.dropzoneClass) {
        if (Array.isArray(props.dropzoneClass)) {
            baseClasses.push(...props.dropzoneClass);
        } else if (typeof props.dropzoneClass === 'string') {
            baseClasses.push(props.dropzoneClass);
        } else {
            baseClasses.push(props.dropzoneClass);
        }
    }
    return baseClasses;
});

const placeholderClasses = computed(() => {
    const baseClasses = ['dropzone-placeholder'];
    if (props.placeholderClass) {
        if (Array.isArray(props.placeholderClass)) {
            baseClasses.push(...props.placeholderClass);
        } else if (typeof props.placeholderClass === 'string') {
            baseClasses.push(props.placeholderClass);
        } else {
            baseClasses.push(props.placeholderClass);
        }
    }
    return baseClasses;
});

function updateChildren(novaListaItens) {
    store.dispatch('landingPage/definirChildrenPorId', {
        idComponente: props.componenteId,
        novaListaItens,
    });
}

function handleAddComponente(event) {
    if (event && event.componenteId) {
        return event;
    }
    return criarNovoComponente(event);
}

function atualizarPropsComponente(idComponente, novasProps) {
    store.dispatch('landingPage/atualizarPropsComponente', {
        idComponente,
        novasProps,
    });
}
</script>

<style scoped lang="scss">
.dropzone {
    width: 100%;
    height: 100%;
    box-sizing: border-box;
    padding: 32px;
}

.dropzone-item {
    position: relative;
}

.dropzone-default {
    display: flex;
    flex-direction: column;
    gap: 12px;
    min-height: 150px;
}

.dropzone-placeholder {
    text-align: center;
    color: #d4d4d4;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 8px;
}

.placeholder {
    color: #d4d4d4;
}

.dropzone-placeholder-default {
    border-radius: 4px;
    height: 100%;
    min-height: 120px;
    width: 100%;
}
</style>
